import { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  UserPlus,
  FileText,
  Upload,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  ChevronLeft,
  CreditCard,
  Layers,
  LifeBuoy,
  Users,
  BookOpen,
} from 'lucide-react';
import NotificationBell from '@/components/notifications/NotificationBell';
import { useLogoutConfirm } from '@/components/ux/useLogoutConfirm';
import ThemeToggle from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import EchoHireLogo from '@/components/branding/EchoHireLogo';
import { useAuth } from '@/context/AuthContext';
import * as apiClient from '@/services/apiClient';
import { API_CONFIG } from '@/config/config.js';
import { hasPermissionForUser } from '@/constants/permissions';
import AdminPlanBanner from '@/components/admin/AdminPlanBanner';
import PlanLimitBanner from '@/components/admin/PlanLimitBanner';
import SubscriptionBlockedBanner from '@/components/admin/SubscriptionBlockedBanner';

const NAV_ITEMS = [
  { label: 'Dashboard',          to: '/admin/dashboard',         icon: LayoutDashboard, permission: 'view_dashboard' },
  { label: 'Job Descriptions',   to: '/admin/templates',         icon: FileText,        permission: 'view_templates' },
  { label: 'Question Sets',      to: '/admin/question-sets',     icon: BookOpen,        permission: 'schedule_interviews' },
  { label: 'Resumes',            to: '/admin/resumes',           icon: Upload,          permission: 'view_resumes' },
  { label: 'Schedule Interview', to: '/admin/schedule',          icon: Calendar,        permission: 'schedule_interviews' },
  { label: 'Pipeline',           to: '/admin/candidate-process', icon: Users,           permission: 'view_pipeline' },
  { label: 'Reports',            to: '/admin/reports',           icon: BarChart3,       permission: 'view_reports' },
  { label: 'User List',          to: '/admin/create-user',       icon: UserPlus,        permission: 'view_team' },
  { label: 'Masters',            to: '/admin/masters',           icon: Layers,          permission: 'manage_masters' },
  { label: 'Support',            to: '/admin/support',           icon: LifeBuoy,        permission: 'use_support' },
  { label: 'My Plan',            to: '/admin/my-plan',           icon: CreditCard,      permission: 'view_plan' },
  { label: 'Settings',           to: '/admin/settings',          icon: Settings },
];


/**
 * Company identity above the navigation.
 *
 * Renders only once a logo or name is available, so a company that has not set
 * one up sees no empty placeholder. Collapsed, it shows the logo alone.
 */
function CompanyBrand({ branding, collapsed }) {
    if (!branding?.companyName && !branding?.logoUrl) return null;

    const name = branding.companyName || '';
    const monogram = name.split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
    const src = branding.logoUrl
        ? (branding.logoUrl.startsWith('http') ? branding.logoUrl : `${API_CONFIG.STATIC_BASE_URL}${branding.logoUrl}`)
        : null;

    return (
        <div className={cn(
            'flex shrink-0 items-center gap-2 border-b border-border px-2 py-2.5',
            collapsed && 'justify-center',
        )}>
            {src ? (
                <img src={src} alt={name}
                    className="h-8 w-8 shrink-0 rounded-lg border border-border bg-background object-contain p-0.5" />
            ) : (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[11px] font-bold text-primary">
                    {monogram}
                </div>
            )}
            {!collapsed && (
                <p className="min-w-0 truncate text-xs font-semibold text-foreground" title={name}>{name}</p>
            )}
        </div>
    );
}

function navItemVisible(user, item) {
  if (!user) return false;
  if (!item.permission) return true;
  return hasPermissionForUser(user, item.permission);
}

function SidebarContent({ collapsed, onNavigate, onLogout, user, onToggleCollapse, branding, avatarUrl }) {
  const avatarSrc = avatarUrl
    ? (avatarUrl.startsWith('http') ? avatarUrl : `${API_CONFIG.STATIC_BASE_URL}${avatarUrl}`)
    : null;
  const initials =
    user?.firstName?.[0]?.toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    'E';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border px-2 py-3">
        <div
          className={cn(
            'flex items-center gap-1.5',
            collapsed ? 'flex-col' : 'flex-row',
            !collapsed && 'min-h-[3rem]'
          )}
        >
          {onToggleCollapse && (
            <Button
              variant="outline"
              size="icon"
              className={cn(
                'h-10 w-10 shrink-0 border-border bg-background text-foreground shadow-none',
                'hover:bg-muted hover:text-foreground hover:border-muted-foreground/40',
                collapsed && 'order-first'
              )}
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <ChevronLeft
                className={cn('h-5 w-5 shrink-0 text-foreground transition-transform', collapsed && 'rotate-180')}
                aria-hidden
              />
            </Button>
          )}
          <NavLink
            to="/admin/dashboard"
            onClick={onNavigate}
            className={cn(
              'flex min-w-0 items-center py-0.5',
              collapsed ? 'w-full justify-center px-0' : 'flex-1 justify-start',
              !onToggleCollapse && 'pl-1'
            )}
          >
            <EchoHireLogo collapsed={collapsed} />
          </NavLink>
        </div>
      </div>

      <CompanyBrand branding={branding} collapsed={collapsed} />

      {!collapsed && (
        <p className="shrink-0 px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
          Workspace
        </p>
      )}
      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden px-2 pb-2 pt-1 overscroll-contain">
        {NAV_ITEMS
          .filter(item => navItemVisible(user, item))
          .map(({ label, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-md py-2 text-[13px] font-medium transition-colors eh-touch-target',
                collapsed ? 'justify-center px-2' : 'border-l-2 pl-2.5 pr-2',
                collapsed
                  ? isActive
                    ? 'bg-muted/70 text-foreground'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                  : isActive
                    ? 'border-primary bg-muted/70 text-foreground'
                    : 'border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              )
            }
          >
            <Icon className="h-[18px] w-[18px] shrink-0 opacity-90" aria-hidden />
            {!collapsed && <span className="truncate">{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto shrink-0 border-t border-border p-2">
        <div
          className={cn(
            'flex flex-col gap-2 rounded-md border border-border/60 bg-muted/30 p-2',
            collapsed && 'items-center border-0 bg-transparent px-1 py-2'
          )}
        >
          <div
            className={cn(
              'flex min-w-0 items-center gap-2',
              collapsed ? 'flex-col' : 'flex-row'
            )}
          >
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                className="h-9 w-9 shrink-0 rounded-md border border-border object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground">
                {initials}
              </div>
            )}
            {!collapsed && user && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.firstName || 'User'}
                </p>
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-9 w-full gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive',
              collapsed && 'flex-col gap-1 px-2 py-2.5 h-auto'
            )}
            onClick={onLogout}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className={cn('text-sm font-medium', collapsed && 'text-[11px]')}>Logout</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user } = useAuth();
  const { requestLogout, LogoutConfirmDialog } = useLogoutConfirm();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Company name and logo for the sidebar. Fetched once here and passed to both
  // sidebar instances rather than fetched twice. Silent on failure — chrome
  // should never surface an error toast.
  const [branding, setBranding] = useState(null);

  // Settings updates localStorage and fires this event; listening keeps the
  // sidebar in step without a reload.
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
  useEffect(() => { setAvatarUrl(user?.avatarUrl || ''); }, [user?.avatarUrl]);
  useEffect(() => {
    const onChange = (e) => setAvatarUrl(e.detail?.avatarUrl || '');
    window.addEventListener('echohire:avatar-changed', onChange);
    return () => window.removeEventListener('echohire:avatar-changed', onChange);
  }, []);
  useEffect(() => {
    let cancelled = false;
    apiClient.getCompanyBranding()
      .then(res => { if (!cancelled) setBranding(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="flex h-dvh max-h-dvh min-h-0 w-full overflow-hidden bg-background">
      {LogoutConfirmDialog}
      <aside
        className={cn(
          'hidden h-full max-h-dvh shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200 md:flex',
          collapsed ? 'w-[88px]' : 'w-64'
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <SidebarContent
            branding={branding}
            avatarUrl={avatarUrl}
            collapsed={collapsed}
            onToggleCollapse={() => setCollapsed((c) => !c)}
            onLogout={requestLogout}
            user={user}
          />
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-label="Close menu"
          onClick={closeMobile}
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-dvh max-h-dvh w-64 flex-col border-r border-border bg-card transition-transform duration-200 md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarContent collapsed={false} onNavigate={closeMobile} onLogout={requestLogout} user={user} branding={branding} avatarUrl={avatarUrl} />
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top header — mobile hamburger + logo on mobile, notification bell always */}
        <header className="relative z-50 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-card/95 px-3 backdrop-blur">
          {/* Hamburger — mobile only */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Logo — mobile only (desktop has sidebar logo) */}
          <NavLink
            to="/admin/dashboard"
            className="flex min-w-0 flex-1 items-center py-1 md:hidden"
            aria-label="Dashboard home"
          >
            <EchoHireLogo className="!max-h-10 !h-10 max-w-[min(100%,11rem)] w-auto" />
          </NavLink>

          {/* Spacer for desktop (pushes bell to the right) */}
          <div className="hidden md:flex flex-1" />

          {/* Theme toggle + Notification bell — always visible */}
          <ThemeToggle size="small" />
          <NotificationBell />
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-3 sm:p-5 lg:p-6">
          <AdminPlanBanner />
          <SubscriptionBlockedBanner />
          <PlanLimitBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
